import type { HPOTerm } from '@/components/common/HPOSearch'
import type { OrderSideNavSection } from '@/components/order/OrderSideNav'
import type { ClinicalSummaryData } from '@/components/order/OrderTypes'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'
import { mapCategoryToType } from '@/utils/orderFormUtils'

export function clinicalSummaryDataFromCanonical(canonical: CanonicalOrderPayload): ClinicalSummaryData {
  return {
    summary: canonical.clinical?.clinicalNote ?? '',
    differentialDiagnosis: canonical.clinical?.differentialDiagnosis ?? '',
    priorGeneticTesting: canonical.clinical?.priorGeneticTesting ?? '',
  }
}

export function indicationTermsFromCanonical(canonical: CanonicalOrderPayload): HPOTerm[] {
  return (canonical.reason?.clinicalDiagnosis ?? []).map((term) => ({
    id: term.id,
    name: term.label,
    hpoCode: term.id,
    type: mapCategoryToType(term.category),
  }))
}

export type IndicationsAttentionTarget = 'none' | 'clinical-summary'

/** Incomplete phenotype review routes to Clinical Summary (Indications is not a separate surface). */
export function indicationsAttentionTarget(
  hasClinicalSummary: boolean,
  hasIndications: boolean,
  reviewBlocksSubmit = false
): IndicationsAttentionTarget {
  if (reviewBlocksSubmit || !hasIndications) {
    return hasClinicalSummary ? 'clinical-summary' : 'none'
  }
  return 'none'
}

/**
 * Order Start hides Indications as its own side-nav row; fold phenotype
 * completeness (and unresolved review) into Clinical Summary instead.
 */
export function sideNavSectionsForMergedClinicalSummary(
  sections: OrderSideNavSection[],
  reviewBlocksSubmit: boolean
): OrderSideNavSection[] {
  const indications = sections.find((section) => section.id === 'order-section-indications')
  return sections
    .filter((section) => section.id !== 'order-section-indications')
    .map((section) => {
      if (section.id !== 'order-section-clinical') return section
      const phenotypesOk = !indications?.required || indications.completed === true
      return {
        ...section,
        required: Boolean(section.required || indications?.required),
        completed: section.completed === true && phenotypesOk && !reviewBlocksSubmit,
      }
    })
}
